import { render } from "preact";
import { useEffect, useState } from "preact/hooks";

import { istDankeadresse, plzAusAdresse, slugAusPfad } from "./adresse";
import { ApiFehler, funnelLaden, type GeladenerFunnel } from "./api";
import { Abschluss } from "./Abschluss";
import { FunnelAnsicht } from "./Funnel";
import { standLaden, type Sitzungsstand } from "./speicher";
import "./stile.css";

/**
 * Der Einstieg.
 *
 * Welcher Funnel geladen wird, ergibt sich aus drei Quellen, in dieser
 * Reihenfolge:
 *
 *   1. `?funnel=...`   für die Vorschau, überschreibt alles
 *   2. der Pfad        `/anfrage/gartenarbeit/` -> `gartenarbeit`
 *   3. `data-funnel`   am Behälter, für eine Einbettung auf fremder Seite
 *
 * Der Pfad ist der Normalfall: Jeder Bereich hat seine eigene Adresse, weil
 * eine Google-Anzeige auf eine Seite zeigt, die genau das anbietet, wonach
 * gesucht wurde. Ein gemeinsamer Funnel mit einer Frage "welches Gewerk denn?"
 * wäre ein Schritt mehr zwischen Klick und Anfrage.
 *
 * Das Attribut bleibt für die Einbettung: ein leeres div, ein Skript, fertig.
 * Kein WordPress, kein Baukasten, keine Abhängigkeit von der Seite drumherum.
 */

function slugErmitteln(behaelter: HTMLElement): string {
  const ausAdresse = new URLSearchParams(window.location.search).get("funnel");
  if (ausAdresse) return ausAdresse;

  const ausPfad = slugAusPfad(window.location.pathname);
  if (ausPfad) return ausPfad;

  return behaelter.dataset["funnel"] ?? "";
}

/**
 * Die Danke-Seite als eigene Adresse.
 *
 * Sie zeigt denselben Abschluss wie der Funnel, nur an einer eigenen Adresse -
 * damit Google Ads einen Seitenaufruf zum Zählen hat. Der Inhalt bleibt dabei
 * im Funnel und nicht in WordPress: Sonst stünde derselbe Text an zwei Stellen
 * und wäre nach der ersten Änderung an einer davon falsch.
 *
 * Eingebunden wird sie mit demselben Skript wie der Funnel, nur mit
 * data-danke am Behälter.
 */
function Dankeseite({ slug }: { slug: string }) {
  const [geladen, setGeladen] = useState<GeladenerFunnel | null>(null);

  useEffect(() => {
    let abgebrochen = false;
    void funnelLaden(slug)
      .then((ergebnis) => {
        if (!abgebrochen) setGeladen(ergebnis);
      })
      .catch(() => {
        // Ohne Funnel keine Texte. Die Seite bleibt leer statt halb.
      });
    return () => {
      abgebrochen = true;
    };
  }, [slug]);

  if (!geladen) return null;

  const referenz = new URLSearchParams(window.location.search).get("ref");

  return (
    <div class="f-wurzel">
      <Abschluss abschluss={geladen.funnel.completion} referenz={referenz} />
    </div>
  );
}

function Start({ slug }: { slug: string }) {
  const [geladen, setGeladen] = useState<GeladenerFunnel | null>(null);
  const [stand, setStand] = useState<Sitzungsstand | null>(null);
  const [fehler, setFehler] = useState<string | null>(null);

  useEffect(() => {
    let abgebrochen = false;

    void (async () => {
      try {
        const ergebnis = await funnelLaden(slug);
        if (abgebrochen) return;

        setStand(
          ergebnis.funnel.settings.persistence.resume_enabled
            ? standLaden(
                ergebnis.funnel.id,
                ergebnis.funnel.settings.persistence.resume_ttl_hours,
              )
            : null,
        );
        setGeladen(ergebnis);
      } catch (ursache) {
        if (abgebrochen) return;
        setFehler(
          ursache instanceof ApiFehler
            ? ursache.message
            : "Das Formular konnte nicht geladen werden.",
        );
      }
    })();

    return () => {
      abgebrochen = true;
    };
  }, [slug]);

  if (fehler !== null) {
    return (
      <div class="f-wurzel">
        <div class="f-danke">
          <h1 class="f-danke__titel">Das Formular ist gerade nicht erreichbar</h1>
          <p class="f-danke__text">{fehler}</p>
        </div>
      </div>
    );
  }

  /**
   * Während des Ladens bewusst nichts.
   *
   * Ein Platzhalter, der nach Bruchteilen einer Sekunde durch die erste Frage
   * ersetzt wird, flackert nur. Der Funnel steht im ersten Bildschirm einer
   * Seite, die selbst gerade erst geladen hat.
   */
  if (!geladen) return null;

  /**
   * Die Postleitzahl von der Startseite in das Feld eintragen, das sie meint.
   *
   * Welcher Schritt das ist, steht nicht fest: Die dreizehn Bereiche nennen
   * ihn "plz", ältere Funnels "postcode". Gesucht wird deshalb der Schritt
   * seines Typs und nicht ein Name - sonst landet die Angabe still im Nichts,
   * sobald jemand einen Funnel anders benennt.
   */
  const plz = plzAusAdresse(window.location.search);
  const plzSchritt = geladen.funnel.steps.find((s) => s.type === "postcode");
  /*
   * Die Form muss zum Schritt passen: Fragt er nur die Postleitzahl ab, ist
   * die Antwort eine Zeichenkette; kommen Ort oder Strasse dazu, ein Objekt.
   * Die falsche Form wuerde beim ersten Tippen ueberschrieben - die
   * vorbelegte Postleitzahl waere weg.
   */
  const vorbelegung =
    plz !== null && plzSchritt
      ? {
          [plzSchritt.id]:
            plzSchritt.address_fields.length === 0 ? plz : { postcode: plz },
        }
      : {};

  return (
    <FunnelAnsicht
      funnel={geladen.funnel}
      slug={slug}
      stand={stand}
      vorbelegung={vorbelegung}
    />
  );
}

/**
 * Wo der Funnel hin soll.
 *
 * Die feste Kennung ist der dokumentierte Weg (docs/12). Der zweite Griff ist
 * für fremde Seiten: Manche Baukästen vergeben eigene Kennungen oder
 * entfernen sie beim Speichern. Ein data-funnel allein genügt dann.
 */
const behaelter =
  document.getElementById("fbs-funnel") ??
  document.querySelector<HTMLElement>("[data-funnel]");

if (behaelter) {
  const slug = slugErmitteln(behaelter);
  if (slug) {
    // data-danke am Behälter: dieselbe Engine, aber nur der Abschluss.
    // Zwei Wege, dieselbe Aussage: das Attribut auf fremden Seiten, der Pfad
    // auf den eigenen.
    const istDanke =
      behaelter.dataset["danke"] !== undefined ||
      istDankeadresse(window.location.pathname);
    render(istDanke ? <Dankeseite slug={slug} /> : <Start slug={slug} />, behaelter);
  } else {
    // Eine stumme leere Seite wäre für den Einbauenden nicht zu deuten.
    console.error(
      "[fbs-funnel] Es fehlt data-funnel am Behälter. Erwartet wird " +
        '<div id="fbs-funnel" data-funnel="kurzname"></div>',
    );
  }
}
